"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BackButton } from "@/components/ui/back-button";
import {
  AlertTriangle,
  DollarSign,
  UserX,
  Shield,
  Star,
  HelpCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";

type IssueCategory = "payment" | "no_show" | "safety" | "quality" | "other";

const CATEGORIES: { value: IssueCategory; label: string; icon: React.ReactNode; description: string }[] = [
  { value: "payment", label: "Payment Issue", icon: <DollarSign className="h-5 w-5" />, description: "Payment didn't go through, wrong amount, or refund needed" },
  { value: "no_show", label: "No Show", icon: <UserX className="h-5 w-5" />, description: "Worker or contractor didn't show up as agreed" },
  { value: "safety", label: "Safety Concern", icon: <Shield className="h-5 w-5" />, description: "Unsafe conditions, missing PPE, or safety incident" },
  { value: "quality", label: "Quality Issue", icon: <Star className="h-5 w-5" />, description: "Work quality below expectations or standards" },
  { value: "other", label: "Other", icon: <HelpCircle className="h-5 w-5" />, description: "Anything else that needs attention" },
];

export default function ReportIssuePage() {
  const [category, setCategory] = useState<IssueCategory | null>(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!category || description.trim().length < 10) return;

    setSubmitting(true);
    setError(null);

    try {
      // Get CSRF token
      const csrfMeta = document.querySelector('meta[name="csrf-token"]');
      const csrfToken = csrfMeta?.getAttribute("content") || "";

      const res = await fetch("/api/issues/report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify({ category, description: description.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to submit report");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <h2 className="text-xl font-bold">Report Submitted</h2>
            <p className="text-center text-sm text-muted-foreground">
              We&apos;ve received your report and will look into it.
              {category === "safety" && (
                <span className="block mt-1 font-medium text-destructive">
                  Safety reports are treated as urgent priority.
                </span>
              )}
            </p>
            <Button onClick={() => router.push("/dashboard")} className="mt-4 h-12 w-full max-w-xs">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      <BackButton />

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Report an Issue
          </CardTitle>
          <CardDescription>
            Something wrong? Let us know and we&apos;ll sort it out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category selection */}
            <div className="space-y-2">
              <Label>What&apos;s the issue?</Label>
              <div className="space-y-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors min-h-[44px] ${
                      category === cat.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className={`mt-0.5 shrink-0 ${category === cat.value ? "text-primary" : "text-muted-foreground"}`}>
                      {cat.icon}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{cat.label}</p>
                      <p className="text-xs text-muted-foreground">{cat.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            {category && (
              <div className="space-y-2">
                <Label htmlFor="description">Tell us what happened</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue in detail..."
                  rows={4}
                  maxLength={2000}
                  className="text-base"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {description.length}/2000 characters (minimum 10)
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3">
                <p className="text-sm text-destructive font-medium">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="h-12 w-full text-base font-semibold"
              disabled={!category || description.trim().length < 10 || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Report"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
